import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FUEL_TYPES, FUEL_TYPE_LABELS, type FuelType } from '@shared/domain';
import { ROLE_LABELS, type Role } from '@shared/roles';
import { api } from '../../api';
import { staffInviteText, whatsappUrl } from '@shared/invites';
import { useSession } from '../../session';
import type { PaymentDetails } from '../../types';
import { Badge, Dialog, ErrorBox, Field, Loading, RupeeInput, day, dt, inr, rupeesToPaise, useBusy } from '../../ui';

interface Location { _id: string; name: string; code: string; address: string; fuelTypes: string[] }
interface Member { membershipId: string; name?: string; phoneMasked: string; roles: Role[]; locationIds: string[]; status: string; pinSet: boolean }

export function Setup() {
  const { can } = useSession();
  return (
    <div className="stack">
      <h1>Pump setup</h1>
      <div className="card"><h2>Accounting</h2><p>Connect your supplier’s Zoho Books account and match invoices and payments.</p><a className="btn" href="/supplier/accounting">Zoho Books setup</a></div>
      {can('supplier.staff.manage') ? <Staff /> : null}
      {can('supplier.locations.manage') ? <Locations /> : null}
      {can('supplier.paymentDetails.manage') ? <PaymentDetailsCard /> : null}
    </div>
  );
}

function Staff() {
  const qc = useQueryClient();
  const { active } = useSession();
  const members = useQuery({ queryKey: ['members'], queryFn: () => api.get<Member[]>('/api/org/members') });
  const locations = useQuery({ queryKey: ['locations'], queryFn: () => api.get<Location[]>('/api/locations') });
  const blank = { name: '', phone: '', role: 'SUPPLIER_ATTENDANT', location: '', pin: '' };
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(blank);
  const [added, setAdded] = useState<typeof blank | null>(null);
  const [pinFor, setPinFor] = useState<Member | null>(null);
  const [pin, setPin] = useState('');
  const { busy, error, run } = useBusy();
  const pump = (id: string) => locations.data?.find((l) => l._id === id)?.name;
  const inviteText = (a: typeof blank) => staffInviteText({ personName: a.name, businessName: active?.orgName ?? 'Your pump', roleLabel: ROLE_LABELS[a.role as Role], attendant: a.role === 'SUPPLIER_ATTENDANT', pumpName: pump(a.location), pinSet: Boolean(a.pin), webBase: window.location.origin });
  const isPumpStaff = (m: Member) => m.roles.some((r) => r.startsWith('SUPPLIER_'));
  return (
    <div className="card">
      <div className="row between"><h2>Staff</h2><button className="btn" onClick={() => { setF(blank); setAdded(null); setOpen(true); }}>+ Add staff</button></div>
      <p className="muted">Attendants can work from the pump’s counter phone with a PIN you set, with no app of their own. They only see indent details at their pump, never balances or payments.</p>
      <ul className="list">
        {members.data?.map((m) => (
          <li key={m.membershipId} className="row between">
            <span><strong>{m.name}</strong> · {m.roles.map((r) => ROLE_LABELS[r]).join(', ')} {m.roles.includes('SUPPLIER_ATTENDANT') ? (m.pinSet ? <Badge tone="ok">PIN set</Badge> : <Badge tone="warn">no PIN yet</Badge>) : null}</span>
            <span className="row">
              <Badge tone={m.status === 'ACTIVE' ? 'ok' : 'bad'}>{m.status.toLowerCase()}</Badge>
              {m.status === 'ACTIVE' && isPumpStaff(m) ? <button className="btn small" onClick={() => { setPin(''); setPinFor(m); }}>{m.pinSet ? 'Change PIN' : 'Set PIN'}</button> : null}
              {m.status === 'ACTIVE' ? <button className="btn small" onClick={() => void run(async () => { const reason = prompt('Reason for removing access?'); if (!reason) return; await api.post(`/api/org/members/${m.membershipId}/disable`, { reason }); await qc.invalidateQueries({ queryKey: ['members'] }); })}>Remove access</button> : null}
            </span>
          </li>
        ))}
      </ul>
      <ErrorBox error={error} />
      <Dialog open={open} title={added ? 'Staff added' : 'Add staff'} onClose={() => setOpen(false)}>
        {added ? (
          <div className="stack">
            <div className="alert ok">{added.name} is added. Send them how to start on WhatsApp.</div>
            <p className="muted" style={{ whiteSpace: 'pre-line' }}>{inviteText(added)}</p>
            <a className="btn primary" href={whatsappUrl(added.phone, inviteText(added))} target="_blank" rel="noreferrer">Send to {added.name.split(' ')[0]} on WhatsApp</a>
            {added.pin ? <p className="muted">Tell them their PIN yourself; it is not in the message.</p> : null}
            <button className="btn" onClick={() => setOpen(false)}>Done</button>
          </div>
        ) : (
          <div className="stack">
            <Field label="Name"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
            <Field label="Mobile number" hint="Used for their WhatsApp message, and if they ever sign in on their own phone."><input inputMode="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
            <Field label="Role"><select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>{(['SUPPLIER_ATTENDANT', 'SUPPLIER_ACCOUNTS', 'SUPPLIER_ADMIN'] as Role[]).map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}</select></Field>
            {f.role === 'SUPPLIER_ATTENDANT' ? (
              <>
                <Field label="Pump"><select value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })}><option value="">Choose…</option>{locations.data?.map((l) => <option key={l._id} value={l._id}>{l.name}</option>)}</select></Field>
                <Field label="Counter PIN (optional)" hint="4–6 digits for the pump's counter phone. Not 1234 or 1111. You can set it later."><input inputMode="numeric" maxLength={6} value={f.pin} onChange={(e) => setF({ ...f, pin: e.target.value.replace(/\D/g, '') })} /></Field>
              </>
            ) : null}
            <ErrorBox error={error} />
            <button className="btn primary" disabled={busy} onClick={() => void run(async () => { await api.post('/api/org/members', { name: f.name, phone: f.phone, roles: [f.role], locationIds: f.location ? [f.location] : [], pin: f.role === 'SUPPLIER_ATTENDANT' && f.pin ? f.pin : undefined }); setAdded(f); await qc.invalidateQueries({ queryKey: ['members'] }); })}>Add</button>
          </div>
        )}
      </Dialog>
      <Dialog open={Boolean(pinFor)} title={`Counter PIN for ${pinFor?.name ?? ''}`} onClose={() => setPinFor(null)}>
        <div className="stack">
          <Field label="New PIN" hint="4–6 digits. They enter it on the pump's counter phone. Tell it to them yourself."><input inputMode="numeric" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} autoFocus /></Field>
          <ErrorBox error={error} />
          <button className="btn primary" disabled={busy || pin.length < 4} onClick={() => void run(async () => { await api.post(`/api/org/members/${pinFor!.membershipId}/pin`, { pin }); setPinFor(null); await qc.invalidateQueries({ queryKey: ['members'] }); })}>Save PIN</button>
        </div>
      </Dialog>
    </div>
  );
}

function Locations() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['locations'], queryFn: () => api.get<Location[]>('/api/locations') });
  const [f, setF] = useState({ name: '', code: '', address: '', fuels: ['PETROL', 'DIESEL'] as string[] });
  const [open, setOpen] = useState(false);
  const { busy, error, run } = useBusy();
  return (
    <div className="card">
      <div className="row between"><h2>Pumps (locations)</h2><button className="btn" onClick={() => setOpen(true)}>+ Add pump</button></div>
      <ul className="list">{q.data?.map((l) => <li key={l._id}><strong>{l.name}</strong> ({l.code}) · {l.fuelTypes.map((t) => FUEL_TYPE_LABELS[t as FuelType]).join(', ')}<div><small>{l.address}</small></div></li>)}</ul>
      <Dialog open={open} title="Add a pump" onClose={() => setOpen(false)}>
        <div className="stack">
          <Field label="Name"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Short code"><input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} maxLength={12} /></Field>
          <Field label="Address"><input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></Field>
          <fieldset><legend>Fuels sold</legend>{FUEL_TYPES.map((t) => <label key={t} className="check"><input type="checkbox" checked={f.fuels.includes(t)} onChange={(e) => setF({ ...f, fuels: e.target.checked ? [...f.fuels, t] : f.fuels.filter((x) => x !== t) })} /> {FUEL_TYPE_LABELS[t]}</label>)}</fieldset>
          <ErrorBox error={error} />
          <button className="btn primary" disabled={busy} onClick={() => void run(async () => { await api.post('/api/locations', { name: f.name, code: f.code, address: f.address, categories: ['FUEL'], fuelTypes: f.fuels }); setOpen(false); await qc.invalidateQueries(); })}>Save</button>
        </div>
      </Dialog>
    </div>
  );
}

function PaymentDetailsCard() {
  const qc = useQueryClient();
  const org = useQuery({ queryKey: ['org'], queryFn: () => api.get<{ paymentDetails?: PaymentDetails }>('/api/org') });
  const [f, setF] = useState({ holderName: '', accountNumber: '', ifsc: '', bankName: '', upiId: '', reason: '' });
  const [open, setOpen] = useState(false);
  const { busy, error, run } = useBusy();
  const pd = org.data?.paymentDetails;
  return (
    <div className="card">
      <h2>Where customers pay you</h2>
      <p className="muted">Customers pay your account directly. Changing these details resets the “checked” mark and tells every customer.</p>
      {pd ? (
        <div className="summary"><dl>
          {pd.bankAccount ? <><dt>Account</dt><dd>{pd.bankAccount.holderName} · {pd.bankAccount.accountNumber} · {pd.bankAccount.ifsc} · {pd.bankAccount.bankName}</dd></> : null}
          {pd.upiId ? <><dt>UPI</dt><dd>{pd.upiId}</dd></> : null}
          <dt>Checked</dt><dd>{pd.verification.status === 'VERIFIED' ? <Badge tone="ok">checked {day(pd.verification.verifiedAt)}</Badge> : <Badge tone="warn">not checked yet</Badge>}</dd>
        </dl></div>
      ) : <p>Not added yet.</p>}
      <button className="btn" onClick={() => setOpen(true)}>{pd ? 'Change details' : 'Add details'}</button>
      <Dialog open={open} title="Bank / UPI details" onClose={() => setOpen(false)}>
        <div className="stack">
          <Field label="Account holder name"><input value={f.holderName} onChange={(e) => setF({ ...f, holderName: e.target.value })} /></Field>
          <Field label="Account number"><input inputMode="numeric" value={f.accountNumber} onChange={(e) => setF({ ...f, accountNumber: e.target.value.replace(/\D/g, '') })} /></Field>
          <Field label="IFSC"><input value={f.ifsc} onChange={(e) => setF({ ...f, ifsc: e.target.value.toUpperCase() })} /></Field>
          <Field label="Bank name"><input value={f.bankName} onChange={(e) => setF({ ...f, bankName: e.target.value })} /></Field>
          <Field label="UPI ID (optional)"><input value={f.upiId} onChange={(e) => setF({ ...f, upiId: e.target.value })} /></Field>
          <Field label="Reason for change"><input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></Field>
          <ErrorBox error={error} />
          <button className="btn primary" disabled={busy || !f.reason} onClick={() => void run(async () => {
            await api.put('/api/org/payment-details', { bankAccount: f.accountNumber ? { holderName: f.holderName, accountNumber: f.accountNumber, ifsc: f.ifsc, bankName: f.bankName } : undefined, upiId: f.upiId || undefined, reason: f.reason });
            setOpen(false);
            await qc.invalidateQueries({ queryKey: ['org'] });
          })}>Save</button>
        </div>
      </Dialog>
    </div>
  );
}
